import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { CLIENT_ID_PREFIX } from '@constants/client-id';

import type { PatientRecords } from '@hooks/api/patient-record';
import { useEmergencyLogin } from '@hooks/api/patient-record';

import { PageBody } from '@layouts/DrilldownStyle';

import {
  ClientNumber,
  ContainerCenter,
  DashSeparator,
  FakeLabel,
  FormOuterDiv,
  LoginError,
  OuterContainerFlex,
  PresetBox,
  Title,
} from './EmergencyLogin.style';

const messages = defineMessages({
  title: {
    id: 'emergencyLogin.title',
    defaultMessage: 'Emergency Access',
  },
  userNotFound: {
    id: 'emergencyLogin.userNotFound',
    defaultMessage: 'No users found matching the provided details',
  },
  clientNumber: {
    id: 'emergencyLogin.clientNumber',
    defaultMessage: 'VITALL Member Number',
  },
  clientLastName: {
    id: 'emergencyLogin.clientLastName',
    defaultMessage: 'Member Last Name',
  },
  dob: {
    id: 'GLOBAL.dob',
    defaultMessage: 'Date of Birth',
  },
  emergencyCall: {
    id: 'emergencyLogin.emergencyCall',
    defaultMessage: 'Emergency Call',
  },
  ambulance: {
    id: 'emergencyLogin.ambulance',
    defaultMessage: 'Ambulance',
  },
  accident: {
    id: 'emergencyLogin.accident',
    defaultMessage: 'Accident and Emergency/ER',
  },
  consultation: {
    id: 'emergencyLogin.consultation',
    defaultMessage: 'Consultation',
  },
  other: {
    id: 'GLOBAL.other',
    defaultMessage: 'Other',
  },
  reasonForAccess: {
    id: 'emergencyLogin.reasonForAccess',
    defaultMessage: 'Reason for Access',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

type FormValues = {
  vitall_user_id: string;
  last_name: string;
  dob: string | null;
};

function EmergencyLogin({
  onLogin,
}: {
  onLogin?: (data: PatientRecords) => void;
}) {
  const { formatMessage } = useIntl();
  const [error, setError] = useState('');
  const [emergencyLogin] = useEmergencyLogin();

  const EmergencyLoginFormSchema = Yup.object().shape({
    vitall_user_id: Yup.string().required(formatMessage(messages.required)),
    last_name: Yup.string().required(formatMessage(messages.required)),
    dob: Yup.string().required(formatMessage(messages.required)),
  });

  async function handleSubmit(values: FormValues) {
    setError('');

    const response = await emergencyLogin({
      input: {
        vitall_user_id: `${CLIENT_ID_PREFIX}${values.vitall_user_id}`,
        last_name: values.last_name,
        dob: values.dob ?? '',
      },
      allergy: true,
      condition: true,
      immunization: true,
      medication_statement: true,
      procedure: true,
      legal_directives: true,
      related_person: true,
      coverage: true,
      patient: true,
      blood_type: true,
      body_observation: true,
    });
    const { data, error } = response;
    if (data && !error) {
      onLogin?.(data);
      return true;
    }

    setError(formatMessage(messages.userNotFound));
    return false;
  }

  return (
    <OuterContainerFlex>
      <ContainerCenter>
        <FormOuterDiv>
          <PageBody disableTransparent>
            <Title>{formatMessage(messages.title)}</Title>

            <Formik
              initialValues={{
                vitall_user_id: '',
                last_name: '',
                dob: null,
              }}
              onSubmit={handleSubmit}
              validationSchema={EmergencyLoginFormSchema}
            >
              {({ isSubmitting, setFieldValue, values }) => (
                <Form>
                  <FakeLabel>{formatMessage(messages.clientNumber)}</FakeLabel>

                  <ClientNumber>
                    <PresetBox>{CLIENT_ID_PREFIX.replace('-', '')}</PresetBox>
                    <DashSeparator />
                    <FormikTextField
                      autoComplete="off"
                      name="vitall_user_id"
                      placeholder="000000"
                      required
                    />
                  </ClientNumber>

                  <FormikTextField
                    autoComplete="off"
                    label={formatMessage(messages.clientLastName)}
                    name="last_name"
                    required
                  />

                  <FormikFhirDateField
                    formats={['date']}
                    label={formatMessage(messages.dob)}
                    name="dob"
                    onChange={(date) => {
                      if (date) {
                        setFieldValue('dob', date);
                      }
                    }}
                    required
                    value={values.dob}
                  />

                  {/* <Field name="reason">
                    {({ field, meta }: FieldProps<string>) => (
                      <Dropdown
                        error={meta.touched ? errors.reason : ''}
                        label={formatMessage(messages.reasonForAccess)}
                        width="100%"
                        {...field}
                        onChange={(value) => setFieldValue('reason', value)}
                      >
                        <option value={1}>
                          {formatMessage(messages.emergencyCall)}
                        </option>
                        <option value={2}>
                          {formatMessage(messages.ambulance)}
                        </option>
                        <option value={3}>
                          {formatMessage(messages.accident)}
                        </option>
                        <option value={4}>
                          {formatMessage(messages.consultation)}
                        </option>
                        <option value={5}>
                          {formatMessage(messages.other)}
                        </option>
                      </Dropdown>
                    )}
                  </Field> */}

                  <RoundedButton
                    disabled={isSubmitting}
                    type="submit"
                    width="100%"
                  >
                    {formatMessage(messages.title)}
                  </RoundedButton>
                  <LoginError>{error}</LoginError>
                </Form>
              )}
            </Formik>
          </PageBody>
        </FormOuterDiv>
      </ContainerCenter>
    </OuterContainerFlex>
  );
}

export default EmergencyLogin;
